import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Container,
  Button
} from '@mui/material';
import {
  Menu as MenuIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  AccountCircle
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSelector from '../common/LanguageSelector/LanguageSelector';
import LiveDot from '../common/LiveDot/LiveDot';
import { signOutUser } from '../../utils/auth';

const StyledAppBar = styled(AppBar)`
  background: linear-gradient(135deg, #3B3486 0%, #2A2561 100%);
`;

const StyledToolbar = styled(Toolbar)`
  justify-content: space-between;
`;

const LogoContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NavContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const NavLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const UserButton = styled(Button)`
  color: white !important;
  text-transform: none;
  font-weight: 500;
  padding: 6px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: rgba(255, 255, 255, 0.1);

  span {
    color: white !important;
  }

  svg {
    color: white !important;
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

interface HeaderProps {
  toggleTheme: () => void;
  isDarkMode: boolean;
  userEmail: string | null;
  setUserEmail: (email: string | null) => void;
}

const Header: React.FC<HeaderProps> = ({ toggleTheme, isDarkMode, userEmail, setUserEmail }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogin = () => {
    navigate('/auth');
  };

  const handleSignOut = async () => {
    try {
      await signOutUser();
      setUserEmail(null);
      handleUserMenuClose();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <StyledAppBar position="fixed">
      <Container maxWidth="lg">
        <StyledToolbar>
          <LogoContainer>
            <LiveDot />
            <Typography
              variant="h6"
              component={Link}
              to="/"
              sx={{ textDecoration: 'none', color: 'white' }}
            >
              XCH.live
            </Typography>
          </LogoContainer>

          {/* Mobile Menu */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              color="inherit"
              onClick={handleMobileMenuOpen}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Desktop Navigation */}
          <NavContainer sx={{ display: { xs: 'none', md: 'flex' } }}>
            <NavLink to="/wallets">{t('common.wallets')}</NavLink>
            <NavLink to="/faq">FAQ</NavLink>
            <NavLink to="/blog">{t('common.blog')}</NavLink>
            
            <IconButton onClick={toggleTheme} sx={{ color: 'white' }}>
              {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
            
            <LanguageSelector />

            {userEmail ? (
              <>
                <UserButton onClick={handleUserMenuOpen}>
                  <AccountCircle />
                  {userEmail}
                </UserButton>
                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={handleUserMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <MenuItem onClick={handleSignOut}>Sign Out</MenuItem>
                </Menu>
              </>
            ) : (
              <Button
                variant="text"
                onClick={handleLogin}
                sx={{ color: 'white', textTransform: 'none' }}
              >
                Sign In
              </Button>
            )}
          </NavContainer>

          {/* Mobile Menu Items */}
          <Menu
            anchorEl={mobileMenuAnchor}
            open={Boolean(mobileMenuAnchor)}
            onClose={handleMobileMenuClose}
          >
            <MenuItem onClick={handleMobileMenuClose}>
              <NavLink to="/wallets" style={{ color: 'inherit' }}>
                {t('common.wallets')}
              </NavLink>
            </MenuItem>
            <MenuItem onClick={handleMobileMenuClose}>
              <NavLink to="/faq" style={{ color: 'inherit' }}>
                FAQ
              </NavLink>
            </MenuItem>
            <MenuItem onClick={handleMobileMenuClose}>
              <NavLink to="/blog" style={{ color: 'inherit' }}>
                {t('common.blog')}
              </NavLink>
            </MenuItem>
            <MenuItem onClick={toggleTheme}>
              {isDarkMode ? t('common.lightMode') : t('common.darkMode')}
            </MenuItem>
            {userEmail ? (
              <MenuItem onClick={handleSignOut}>
                <Box sx={{ color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountCircle />
                  <Typography color="inherit">Sign Out</Typography>
                  <Typography color="inherit" sx={{ opacity: 0.8 }}>({userEmail})</Typography>
                </Box>
              </MenuItem>
            ) : (
              <MenuItem onClick={handleLogin}>Sign In</MenuItem>
            )}
          </Menu>
        </StyledToolbar>
      </Container>
    </StyledAppBar>
  );
};

export default Header; 